import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { RootState } from '../../../store/store';
import { registerTransportation } from '../../../apollo/mutations/registerTransportation';
import { Loader } from '../../../components/loader/Loader';
import { Box, Flex } from '@radix-ui/themes';
import { getTranslationByPath } from '../../../utils/translations';
import { Footer } from '../../../layout/footer/Footer';
import { MainContent } from '../../../layout/content/MainContent';
import { getTransportation } from '../../../apollo/queries/getTransportation';
import { setFetchedTransportation } from '../../../store/slices/transportation/transportationSlice';
import {
  getPushNotificationSubscription,
  isPushNotificationSupported
} from '../../../utils/notification';
import { subscribeToTransportationPushNotification } from '../../../apollo/mutations/subscribeToTransportation';


export type RegistrationTemplateType = {
  transitionUrl: TransitionUrl
  registrationSuccessText: TranslationPath
  registrationErrorText: TranslationPath
  infoText: TranslationPath
  backToStartText: TranslationPath
  checkImage: string
  mobileImage: string
}

type Props = {
  page: Page<RegistrationTemplateType>
}


export const RegistrationTemplate = (props: Props): React.ReactElement => {
  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)

  const language = useLanguage();
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isRegistrationError, setRegistrationError] = useState<boolean>(false)

  const subscribeToPushNotifications = async (transportationId: number) => {
    try {
      if (isPushNotificationSupported()) {
        const subscription = await getPushNotificationSubscription()
        if (subscription) {
          await subscribeToTransportationPushNotification({
            transportationId,
            subscription,
            qtirLanguageId: language.qtirLanguageId,
            eventType: 'CALL_TRANSPORTATION',
          })
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    const init = async () => {
      if (!fetchedTransportation) {
        return
      }

      const data = {
        languageId: language.qtirLanguageId,
        driverName: transportationForm.driverName,
        phoneNumber: transportationForm.phoneNumber,
        transportationTypeId: fetchedTransportation.transportationTypeId,
        productId: transportationForm.productId
          ? Number(transportationForm.productId)
          : null,
        registrationNumber: transportationForm.registrationNumber,
        announcementId: fetchedTransportation?.announcementId,
        driverIdNo: fetchedTransportation?.driverIdNo,
        driverIdType: fetchedTransportation?.driverIdType,
      }
      
      console.log(data)

      try {
        const result = await registerTransportation(data)
        setRegistrationError(!result.data?.registerTransportation.transportationId)
        if (result.data?.registerTransportation.transportationId) {
          const findTransportationResult = await getTransportation({
            id: result.data?.registerTransportation.transportationId,
            qtirLanguageId: language.qtirLanguageId,
          })

          const foundTransportation = findTransportationResult.data.getTransportation.transportation
          dispatch(setFetchedTransportation(foundTransportation))
          subscribeToPushNotifications(result.data?.registerTransportation.transportationId)
        }
      } catch (e) {
        console.log(e)
        setRegistrationError(true)
      }
      setIsLoading(false)
    }
    init()
  }, []);

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <MainContent>
        <Flex direction="column" gap="1" width="100%" className="items-center">
          <Box maxWidth="500px" className="w-full flex flex-col items-center text-center">
            {!isRegistrationError && (
              <>
                <img src={`${process.env.APP_DATA_LINK + props.page.data.checkImage}`} className="w-16 h-16 mb-5" alt=""/>

                <div dangerouslySetInnerHTML={{ __html: getTranslationByPath(language.translations, props.page.data.registrationSuccessText) }} />

                <img src={`${process.env.APP_DATA_LINK + props.page.data.mobileImage}`} className="w-16 h-16 mb-5 mt-10" alt=""/>

                <div dangerouslySetInnerHTML={{ __html: getTranslationByPath(language.translations, props.page.data.infoText) }} />
              </>
            )}
            {isRegistrationError && (
              <p>
                {getTranslationByPath(language.translations, props.page.data.registrationErrorText)}
              </p>
            )}
          </Box>
        </Flex>
      </MainContent>

      <Footer
        backUrl={isRegistrationError ? props.page.footer?.back : undefined}
        onContinue={
          isRegistrationError
            ? undefined
            : () => { navigate(props.page.data.transitionUrl, { state: { fromRegistration: true }, replace: true }) }
        } />
    </>
  )
}
